import axios from 'axios';
import { ref } from 'vue';

const API_URL = process.env.VUE_APP_API_URL || '/api';

// Global loading state - accessible from anywhere
const isLoading = ref(false);
let activeRequests = 0;

/**
 * Shared API service with configured axios instance
 * Provides common configuration and interceptors for all API calls
 * Manages global loading state automatically
 */
class ApiService {
  constructor() {
    // Create axios instance with base configuration
    // If VUE_APP_API_URL is not set, default to /api for same-origin requests
    this.api = axios.create({
      baseURL: API_URL,
      timeout: 30000, // Increased from 10s to 30s to handle slow queries
      headers: {
        'Content-Type': 'application/json',
      }
    });

    // Setup request interceptor for authentication and loading state
    this.api.interceptors.request.use(
      async config => {
        // Only increment counter for non-retry requests to prevent double counting
        if (!config._isRetry) {
          activeRequests++;
          isLoading.value = true;
        }
        
        console.log(`🔄 API Request starting: ${config.method?.toUpperCase()} ${config.url} (Active: ${activeRequests}) ${config._isRetry ? '[RETRY]' : ''}`);
        
        const accessToken = localStorage.getItem('accessToken');
        if (accessToken) {
          config.headers = {
            ...config.headers,
            Authorization: `Bearer ${accessToken}`
          };
        }
        return config;
      },
      error => {
        // Decrement on request error
        activeRequests = Math.max(0, activeRequests - 1);
        if (activeRequests === 0) {
          isLoading.value = false;
        }
        console.log(`❌ API Request error: (Active: ${activeRequests})`);
        return Promise.reject(error);
      }
    );

    // Setup response interceptor for error handling and loading state
    this.api.interceptors.response.use(
      response => {
        // Decrement active requests counter on success
        activeRequests = Math.max(0, activeRequests - 1);
        if (activeRequests === 0) {
          isLoading.value = false;
        }
        console.log(`✅ API Request completed: ${response.config.method?.toUpperCase()} ${response.config.url} (Active: ${activeRequests})`);
        return response;
      },
      async error => {
        const originalRequest = error.config;
        
        // Always decrement counter first for the failed request
        activeRequests = Math.max(0, activeRequests - 1);
        
        // If error is 401 (Unauthorized) and not a retry
        if (error.response && error.response.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          try {
            // Try to refresh the token
            const refreshToken = localStorage.getItem('refreshToken');
            if (refreshToken) {
              const refreshUrl = API_URL ? `${API_URL}/v1/auth/refresh-token` : '/api/v1/auth/refresh-token';
              const response = await axios.post(refreshUrl, {
                refreshToken,
                rememberMe: true
              });
              
              if (response.data.accessToken) {
                localStorage.setItem('accessToken', response.data.accessToken);
                
                // Update the Authorization header with the new token
                originalRequest.headers['Authorization'] = `Bearer ${response.data.accessToken}`;
                
                // Mark this as a retry to prevent the request interceptor from double-counting
                originalRequest._isRetry = true;
                
                // Retry the original request
                return this.api(originalRequest);
              }
            }
          } catch (err) {
            // If refresh token fails, clear tokens and redirect to login
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            window.location.href = '/login';
            
            // Check if we need to update loading state
            if (activeRequests === 0) {
              isLoading.value = false;
            }
            return Promise.reject(error);
          }
        }
        
        // Update loading state after decrementing counter
        if (activeRequests === 0) {
          isLoading.value = false;
        }
        console.log(`❌ API Request failed: ${error.config?.method?.toUpperCase()} ${error.config?.url} (Active: ${activeRequests})`);
        return Promise.reject(error);
      }
    );
  }

  /**
   * GET request
   * @param {string} url - API endpoint
   * @param {Object} config - Axios config
   * @returns {Promise} - Promise with response
   */
  get(url, config = {}) {
    return this.api.get(url, config);
  }

  /**
   * POST request
   * @param {string} url - API endpoint
   * @param {Object} data - Request data
   * @param {Object} config - Axios config
   * @returns {Promise} - Promise with response
   */
  post(url, data = {}, config = {}) {
    return this.api.post(url, data, config);
  }

  /**
   * PUT request
   * @param {string} url - API endpoint
   * @param {Object} data - Request data
   * @param {Object} config - Axios config
   * @returns {Promise} - Promise with response
   */
  put(url, data = {}, config = {}) {
    return this.api.put(url, data, config);
  }

  /**
   * DELETE request
   * @param {string} url - API endpoint
   * @param {Object} config - Axios config
   * @returns {Promise} - Promise with response
   */
  delete(url, config = {}) {
    return this.api.delete(url, config);
  }

  /**
   * PATCH request
   * @param {string} url - API endpoint
   * @param {Object} data - Request data
   * @param {Object} config - Axios config
   * @returns {Promise} - Promise with response
   */
  patch(url, data = {}, config = {}) {
    return this.api.patch(url, data, config);
  }

  /**
   * Get global loading state
   * @returns {Ref<boolean>} - Reactive loading state
   */
  getLoadingState() {
    return isLoading;
  }

  /**
   * Get active requests count (for debugging)
   * @returns {number} - Number of active requests
   */
  getActiveRequestsCount() {
    return activeRequests;
  }

  /**
   * Reset loading state (for debugging/recovery)
   * Use this if loading state gets stuck
   */
  resetLoadingState() {
    console.warn('🔧 Manually resetting loading state');
    activeRequests = 0;
    isLoading.value = false;
  }

  /**
   * Get debug info about loading state
   * @returns {Object} - Debug information
   */
  getLoadingDebugInfo() {
    return {
      isLoading: isLoading.value,
      activeRequests,
      timestamp: new Date().toISOString()
    };
  }
}

// Export singleton instance
export default new ApiService();
